<template>
  <div
    class="bg-white modal-card rounded"
    style="width: 960px; max-width: calc(100vw - 2rem); margin: 0 auto;"
  >
    <div class="bg-primary-100 p-4 text-xl">{{ modalTitle }}</div>
    <div class="grid grid-cols-4 gap-3 p-5 overflow-y-auto" style="max-height: 80vh;">
      <div class="col-span-4 sm:col-span-2">
        <t-input-group label="Document">
          <t-input v-model="eventForm.title" type="text" id="title"/>
        </t-input-group>
      </div>
      <div class="col-span-4 sm:col-span-2">
        <t-input-group label="Date">
          <t-datepicker
            timepicker
            v-model="date"
            dateFormat="Y-m-d H:i:S"
            userFormat="j F, Y H:i"
            clearable
            required
          />
        </t-input-group>
      </div>
      <div class="col-span-4">
        <t-input-group label="Description">
          <t-textarea
            rows="6"
            name="description"
            v-model="eventForm.description"
            maxlength="256"
          ></t-textarea>
        </t-input-group>
      </div>

      <div class="col-span-4 sm:col-span-2" v-if="usersGroups && usersGroups.length">
        <t-input-group label="Groupes (optionnel)">
          <t-rich-select
            multiple
            :close-on-select="false"
            :options="usersGroups"
            placeholder="Sélectionner des groupes"
            v-model="groupsSelected"
          />
        </t-input-group>
      </div>

      <div class="col-span-4 sm:col-span-2">
        <t-input-group label="Documents">
          <b-upload v-model="files" multiple @change="fileError = false">
            <a class="button" v-bind:class="fileError && files.length === 0 ? 'is-danger' : 'is-primary'">
              <b-icon icon="upload"></b-icon>
              <span>Ajouter des documents</span>
            </a>
          </b-upload>
        </t-input-group>
      </div>
      <div v-if="files && files.length > 0" class="col-span-4 sm:col-span-2">
        <ul>
          <li v-for="(file, i) in files" :key="i" class="flex justify-between pr-3">
            <span class="" v-if="file.name">- {{ file.name }}</span>
            <button type="button" @click.prevent="removeFile(i)">
              <i class="fas fa-trash text-red-600 cursor-pointer"></i>
            </button>
          </li>
        </ul>
      </div>
      <span class="col-span-4 font-bold block text-red-500" v-if="fileError">{{ fileError }}</span>
    </div>
    <div class="bg-primary-100 p-4 flex flex-wrap items-center justify-between gap-3 mt-3">
      <label class="flex items-center gap-2 font-semibold text-gray-700">
        <t-toggle
          v-model="eventForm.is_enabled"
          :value="true"
          :uncheckedValue="false"
        />
        <span>{{ eventForm.is_enabled ? 'Activé' : 'Désactivé' }}</span>
      </label>
      <div class="flex gap-3">
        <t-button class="m-0" variant="secondary" @click="$parent.close()">Annuler</t-button>
        <t-button @click="sendFile()">Envoyer</t-button>
      </div>
    </div>
  </div>
</template>

<script>
import { http } from '@/services/api.service'
import { format, isValid, parseISO } from 'date-fns'

export default {
  name: 'app-events-one',
  props: ['currentEvent'],
  data () {
    return {
      eventForm: {
        title: '',
        description: '',
        content_to_create: '',
        is_enabled: true
      },
      date: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
      files: [],
      modalTitle: `Création d'un document`,
      fileError: null,
      usersGroups: [],
      groupsSelected: []
    }
  },
  beforeMount () {
    if (this.currentEvent) {
      this.eventForm.title = this.currentEvent.title
      this.eventForm.description = this.currentEvent.description
      this.eventForm.is_enabled = Object.prototype.hasOwnProperty.call(this.currentEvent, 'is_enabled')
        ? this.toBoolean(this.currentEvent.is_enabled)
        : true
      this.date = this.currentEvent.event_date
      this.modalTitle = `Modification d'un document`
      this.files = Array.isArray(this.currentEvent.files) ? [...this.currentEvent.files] : []
      // Preselect groups if provided by API
      if (this.currentEvent.notifications) {
        this.currentEvent.notifications.forEach(n => {
          if (n && n.group && n.group.uuid) {
            this.groupsSelected.push(n.group.uuid)
          }
        })
      }
    }
    // Fetch groups for the domain
    http.get('/groups').then(({ data }) => {
      this.usersGroups = (data || []).map(g => ({ value: g.uuid, text: g.label }))
    }).catch(() => { this.usersGroups = [] })
  },
  methods: {
    sendFile () {
      const formData = this.setFormData()
      if (!formData) {
        this.$buefy.notification.open({
          duration: 3500,
          message: 'Veuillez renseigner une date valide.',
          position: 'is-bottom-left',
          type: 'is-warning',
          hasIcon: true,
          icon: 'calendar-times'
        })
        return
      }

      let error = false
      if (this.currentEvent) {
        http.post(`/events/${this.currentEvent.uuid}`, formData, {
          headers: { 'Content-Type': `multipart/form-data` }
        }).then(({ data }) => {
          this.$emit('updated', data, { isEnabled: this.eventForm.is_enabled })
        }).catch(err => {
          error = true
          if (err.response && err.response.status === 422) {
            this.$buefy.notification.open({
              duration: 3500,
              message: 'Une erreur est survenue lors de l\'édition de ce document.',
              position: 'is-bottom-left',
              type: 'is-warning',
              hasIcon: true,
              icon: 'server'
            })
          }
        }).finally(() => {
          if (!error) this.$parent.close()
        })
      } else {
        http.post('/events', formData, {
          headers: { 'Content-Type': `multipart/form-data` }
        }).then(({ data }) => {
          this.$emit('created', data, { isEnabled: this.eventForm.is_enabled })
        }).catch(err => {
          error = true
          if (err.response && err.response.status === 422) {
            this.$buefy.notification.open({
              duration: 3500,
              message: 'Une erreur est survenue lors de la création de ce document.',
              position: 'is-bottom-left',
              type: 'is-warning',
              hasIcon: true,
              icon: 'server'
            })
          }
        }).finally(() => {
          if (!error) this.$parent.close()
        })
      }
    },
    setFormData () {
      const eventDate = this.formatEventDate(this.date)
      if (!eventDate) return null

      const formData = new FormData()
      Object.keys(this.eventForm).forEach(key => {
        const value = key === 'is_enabled' ? (this.eventForm[key] ? '1' : '0') : this.eventForm[key]
        formData.append(key, value)
      })
      formData.append('event_date', eventDate)
      formData.append('sync_files', '1')

      let realFiles = []
      let keepUuids = []
      this.files.forEach(file => {
        if (
          file &&
          file.name &&
          typeof file.name === 'string' &&
          typeof file.size === 'number' &&
          typeof file.type === 'string' &&
          (typeof File !== 'undefined' ? file instanceof File : true)
        ) {
          realFiles.push(file)
        } else if (file && file.uuid) {
          keepUuids.push(file.uuid)
        }
      })

      realFiles.forEach(file => formData.append('files[]', file))
      keepUuids.forEach(uuid => formData.append('keepFiles[]', uuid))

      this.groupsSelected.forEach(groupUuid => {
        formData.append('groupUuids[]', groupUuid)
      })

      return formData
    },
    formatEventDate (value) {
      const rawValue = typeof value === 'string' ? value.trim() : value
      if (!rawValue) return null

      let parsedDate
      if (rawValue instanceof Date) {
        parsedDate = rawValue
      } else if (typeof rawValue === 'string') {
        parsedDate = parseISO(rawValue.replace(' ', 'T'))
      } else {
        parsedDate = new Date(rawValue)
      }

      return isValid(parsedDate) ? format(parsedDate, 'yyyy-MM-dd HH:mm:ss') : null
    },
    removeFile (i) {
      this.files.splice(i, 1)
    },
    toBoolean (value) {
      return [true, 1, '1', 'true'].includes(value)
    }
  }
}
</script>
